import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/logo.png';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import * as XLSX from 'xlsx';
import './AHECCViewer.css';

const AHECCViewer = () => {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const initRef = useRef(false);
    const aheccDataRef = useRef([]);
    const isMounted = useRef(false);
    const [aheccFilter, setAheccFilter] = useState('');
    const [descriptionFilter, setDescriptionFilter] = useState('');
    const [chapterFilter, setChapterFilter] = useState('');
    const [aheccData, setAheccData] = useState([]);
    const isDrawing = useRef(false);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false); // State for dropdown
    const navigate = useNavigate();

    const expectedHeaders = useMemo(() => ['Chapter', 'Heading', 'AHECC', 'UQ', 'Description'], []);

    const applyStyles = () => {
        const isDarkMode = $('body').hasClass('dark-mode');
        $('.custom-filter-input, .chapter-filter-select').css({
            height: '35px',
            borderRadius: '6px',
            padding: '8px 12px',
            width: '200px',
            fontSize: '14px',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            background: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#fff' : '#495057',
            boxSizing: 'border-box',
            lineHeight: '1.5',
            marginRight: '10px'
        });
        $('.dt-paging button.dt-paging-button').css({
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid ' + (isDarkMode ? '#777' : '#ced4da'),
            borderRadius: '6px',
            backgroundColor: isDarkMode ? '#555' : '#fff',
            color: isDarkMode ? '#ccc' : '#495057',
            cursor: 'pointer',
            margin: '0 4px',
            boxSizing: 'border-box',
            transition: 'background-color 0.2s, color 0.2s'
        });
        $('.dt-paging button.dt-paging-button:not(.disabled):hover').css({
            backgroundColor: isDarkMode ? '#666' : '#f0f4f8',
            color: isDarkMode ? '#fff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.current').css({
            backgroundColor: isDarkMode ? '#007bff' : '#007bff',
            color: '#fff',
            borderColor: isDarkMode ? '#007bff' : '#007bff'
        });
        $('.dt-paging button.dt-paging-button.disabled').css({
            cursor: 'not-allowed',
            opacity: '0.5'
        });
        $('#aheccTable').addClass('table-visible');
    };

    useEffect(() => {
        if (isMounted.current) return;
        isMounted.current = true;

        const loadAheccData = async () => {
            if (aheccDataRef.current.length > 0) {
                return;
            }

            try {
                const response = await fetch('/AHECC.xlsx');
                if (!response.ok) throw new Error('Failed to fetch AHECC data');
                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) throw new Error('Invalid Excel file');

                let loadedAheccData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                let lastAhecc = '';
                loadedAheccData = loadedAheccData.map(row => {
                    const newRow = {};
                    expectedHeaders.forEach(header => {
                        let value = row[header] !== undefined ? String(row[header]).trim() : '';
                        if (header === 'Chapter') {
                            value = value.padStart(2, '0');
                        }
                        newRow[header] = value;
                    });
                    if (newRow['AHECC']) {
                        lastAhecc = newRow['AHECC'];
                    }
                    newRow.parentAhecc = lastAhecc;
                    return newRow;
                });

                aheccDataRef.current = loadedAheccData;
                setAheccData(loadedAheccData);
            } catch (error) {
                console.error('Error loading AHECC data:', error);
                alert('Failed to load AHECC data. Please try again later.');
                aheccDataRef.current = [];
                setAheccData([]);
            }

            if (!tableRef.current) return;

            if (initRef.current && dataTableRef.current) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
                initRef.current = false;
            }

            applyStyles();

            if (!$.fn.DataTable.isDataTable(tableRef.current)) {
                try {
                    dataTableRef.current = $(tableRef.current).DataTable({
                        columns: expectedHeaders.map(header => ({
                            title: header,
                            data: header,
                            render: (data, type, row, meta) => {
                                return data || '';
                            }
                        })),
                        columnDefs: [
                            { width: '100px', targets: 0 },
                            { width: '120px', targets: 1 },
                            { width: '100px', targets: 2 },
                            { width: '40px', targets: 3 },
                            { width: 'auto', targets: 4 }
                        ],
                        pageLength: 15,
                        lengthMenu: [15, 25, 50, 100],
                        searching: true,
                        ordering: true,
                        order: [[0, 'asc']],
                        responsive: true,
                        language: {
                            search: '',
                            searchPlaceholder: 'Filter records...',
                            lengthMenu: 'Show _MENU_ entries',
                        },
                        dom: '<"table-header"l>t<"table-footer"ip>',
                        deferRender: true
                    });
                    initRef.current = true;
                    dataTableRef.current.clear().rows.add(aheccDataRef.current).draw();
                    dataTableRef.current.on('draw', () => {
                        applyStyles();
                        isDrawing.current = false;
                    });
                    setIsTableInitialized(true);
                } catch (error) {
                    console.error('Error initializing DataTable:', error);
                    alert('Failed to initialize table.');
                }
            }
        };

        loadAheccData();

        return () => {
            if (dataTableRef.current && !document.getElementById('aheccTable')) {
                try {
                    dataTableRef.current.destroy(false);
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
            }
            $.fn.dataTable.ext.search = [];
            isMounted.current = false;
        };
    }, [expectedHeaders]);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    // Populate chapter options
    const chapterOptions = useMemo(() => {
        const chapters = [...new Set(aheccData.map(row => row['Chapter']))].filter(ch => ch).sort();
        return ['', ...chapters];
    }, [aheccData]);

    useEffect(() => {
        if (!dataTableRef.current || !isTableInitialized) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (isDrawing.current) {
                return;
            }
            isDrawing.current = true;

            $.fn.dataTable.ext.search = [];

            const filterFunction = (settings, data, dataIndex, rowData, counter) => {
                if (settings.nTable.id !== 'aheccTable') return true;
                const ahecc = String(data[2] || '').toLowerCase();
                const parentAhecc = String(rowData.parentAhecc || '').toLowerCase();
                const description = String(data[4] || '').toLowerCase();
                const chapter = String(data[0] || '');
                const aheccFilterValue = aheccFilter.toLowerCase().trim();
                const descriptionFilterValue = descriptionFilter.toLowerCase().trim();
                const chapterFilterValue = chapterFilter;

                let aheccMatch = true;
                let descriptionMatch = true;
                let chapterMatch = true;

                if (aheccFilterValue) {
                    aheccMatch = ahecc.includes(aheccFilterValue) || parentAhecc.includes(aheccFilterValue);
                }

                if (descriptionFilterValue) {
                    descriptionMatch = description.includes(descriptionFilterValue);
                }

                if (chapterFilterValue) {
                    chapterMatch = chapter === chapterFilterValue;
                }

                return aheccMatch && descriptionMatch && chapterMatch;
            };

            $.fn.dataTable.ext.search.push(filterFunction);

            try {
                dataTableRef.current
                    .column(2)
                    .search('')
                    .column(4)
                    .search('')
                    .draw('full-reset');
            } catch (error) {
                console.error('Error during DataTable filter draw:', error);
                isDrawing.current = false;
            }
        }, 400);

        return () => clearTimeout(timeoutId);
    }, [aheccFilter, descriptionFilter, chapterFilter, isTableInitialized]);

    const handleAheccFilterChange = useCallback((e) => {
        setAheccFilter(e.target.value);
    }, []);

    const handleDescriptionFilterChange = useCallback((e) => {
        setDescriptionFilter(e.target.value);
    }, []);

    const handleChapterFilterChange = useCallback((e) => {
        setChapterFilter(e.target.value);
    }, []);

    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    const resetTable = useCallback(() => {
        if (dataTableRef.current) {
            setAheccFilter('');
            setDescriptionFilter('');
            setChapterFilter('');
            dataTableRef.current
                .column(2)
                .search('')
                .column(4)
                .search('')
                .draw();
            dataTableRef.current.page(0).draw('page');
            applyStyles();
        }
    }, []);

    return (
        <div className="ahecc-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src={logo} alt="Customs Pro AI Logo" />
                    <h1>Customs Export AHECC</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span className="active">Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item active" onClick={() => setIsProDropdownOpen(false)}>AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>Customs Depot Codes</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item"onClick={() => setIsProDropdownOpen(false)}>Quarantine Depot Codes</Link>
                                <Link to="/history" className="dropdown-item" onClick={() => setIsProDropdownOpen(false)}>History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>        
            </header>
            <div className="main-content">
                <div className="table-panel">
                    <div className="table-header">
                        <div id="customFilter">
                            <select
                                value={chapterFilter}
                                onChange={handleChapterFilterChange}
                                className="chapter-filter-select"
                            >
                                <option value="">All Chapters</option>
                                {chapterOptions.filter(ch => ch).map(chapter => (
                                    <option key={chapter} value={chapter}>{chapter}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Filter AHECC..."
                                value={aheccFilter}
                                onChange={handleAheccFilterChange}
                                className="custom-filter-input"
                            />
                            <input
                                type="text"
                                placeholder="Filter Description..."
                                value={descriptionFilter}
                                onChange={handleDescriptionFilterChange}
                                className="custom-filter-input"
                            />
                        </div>
                        <button id="resetTableBtn" onClick={resetTable}>Reset Table</button>
                    </div>
                    <table id="aheccTable" className="display" ref={tableRef}></table>
                </div>
            </div>
        </div>
    );
};

export default AHECCViewer;